import { useReviseFlowItem } from '@reelplicate/api-client/flow-runs';
import type { FlowItemDto, FlowRunDto } from '@reelplicate/api-client/model';
import { Button, Field, Stack, TextArea, toast } from '@reelplicate/ui';
import { useState } from 'react';

/**
 * "Rewrite line" on a held script: edit its hook and body, then send it back
 * through the guardrail that held it. Used on the run canvas and on Home.
 */
export function RewriteLine({
  workspaceId,
  item,
  onCancel,
  onRevised,
}: {
  workspaceId: string;
  item: FlowItemDto;
  onCancel: () => void;
  /** The run after the rewrite, checking the item again. */
  onRevised: (run: FlowRunDto) => void;
}) {
  const revise = useReviseFlowItem();
  const [hook, setHook] = useState(text(item.payload.hook));
  const [body, setBody] = useState(text(item.payload.body));

  const save = async () => {
    try {
      onRevised(
        await revise.mutateAsync({
          id: workspaceId,
          itemId: item.id,
          data: { payload: { ...item.payload, hook, body } },
        }),
      );
      toast.success('Rewritten - checking it again');
    } catch {
      return; // Shown as a toast.
    }
  };

  return (
    <Stack gap={3}>
      <Field label="Hook">
        <TextArea value={hook} onChange={(event) => setHook(event.target.value)} />
      </Field>
      <Field label="Body">
        <TextArea value={body} onChange={(event) => setBody(event.target.value)} />
      </Field>
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" loading={revise.isPending} onClick={() => void save()}>
          Check again
        </Button>
      </div>
    </Stack>
  );
}

/** Only scripts can be rewritten in place. */
export const canRewrite = (item: FlowItemDto) => item.dataType === 'script';

const text = (value: unknown) => (typeof value === 'string' ? value : '');
